'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import TokenBalanceIndicator from '@/components/common/TokenBalanceIndicator';
import UserMenuDropdown from '@/components/common/UserMenuDropdown';
import Image from 'next/image';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

interface NavigationItem {
  label: string;
  path: string;
  icon: string;
}

const Header = () => {
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { loading, isAuthenticated, userProfile } = useAuth();
  const [institutionName, setInstitutionName] = useState<string | null>(null);

  // Cargar nombre de la institución si el usuario es institucional
  useEffect(() => {
    const institutionId = (userProfile as any)?.institution_id;
    if (!institutionId) {
      setInstitutionName(null);
      return;
    }
    supabase
      .from('institutions')
      .select('name')
      .eq('id', institutionId)
      .single()
      .then(({ data }) => setInstitutionName(data?.name ?? null));
  }, [(userProfile as any)?.institution_id]);

  const aiCoachPath = isAuthenticated
    ? '/ai-business-coach'
    : '/login?redirect=%2Fai-business-coach';

  const navigationItems: NavigationItem[] = [
    { label: 'AI Coach', path: aiCoachPath, icon: 'SparklesIcon' },
    { label: 'Mentoría', path: '/mentoria', icon: 'AcademicCapIcon' },
    { label: 'Dashboard', path: '/user-dashboard', icon: 'ChartBarIcon' },
    { label: 'Pricing', path: '/pricing-page', icon: 'CurrencyDollarIcon' },
    { label: 'Sobre Nosotros', path: '/about-us', icon: 'InformationCircleIcon' },
  ];

  const isActive = (path: string) => {
    if (path === aiCoachPath) {
      return (
        pathname === '/start' ||
        pathname.startsWith('/ai-business-coach') ||
        pathname.startsWith('/ai-idea-coach') ||
        pathname.startsWith('/brainstorm-coach') ||
        pathname.startsWith('/obx')
      );
    }
    return pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-1000 border-b border-border bg-[rgba(255,255,255,0.7)] backdrop-blur-xl shadow-elevation-sm">
      <div className="flex items-center justify-between h-14 sm:h-16 px-4 sm:px-6 max-w-7xl mx-auto">
        {/* Logo Section */}
        <div className="flex items-center gap-3">
          <Link href="/landing-page" className="flex items-center space-x-3 transition-all hover:opacity-80">
            <div className="relative w-9 h-9 sm:w-10 sm:h-10">
              <Image
                src="/assets/images/LynMet-1769196309420.png"
                alt="LynMet Logo"
                fill
                sizes="40px"
                className="object-contain"
                priority
              />
            </div>
            <span
              className="hidden text-lg font-bold text-transparent bg-clip-text sm:block sm:text-xl"
              style={{ backgroundImage: 'var(--gradient-app)' }}
            >
              LynMet
            </span>
          </Link>

          {/* Badge institucional */}
          {isAuthenticated && institutionName && (
            <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-500/12 border border-cyan-400/25 text-cyan-700 text-[11px] font-semibold">
              <Icon name="BuildingLibraryIcon" size={12} className="text-cyan-600" />
              {institutionName}
            </span>
          )}
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-1">
          {navigationItems.map((item) => (
            <Link
              key={item.path}
              href={item.path}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all font-medium ${
                isActive(item.path)
                  ? 'btn-primary-app text-[color:var(--color-primary-foreground)] shadow-elevation-md'
                  : 'text-foreground hover:bg-white/70'
              }`}
            >
              <Icon name={item.icon as any} size={20} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Right Section */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          {!loading && isAuthenticated ? (
            <>
              <TokenBalanceIndicator className="scale-90 sm:scale-100 origin-right" />
              <UserMenuDropdown className="scale-90 sm:scale-100 origin-right" />
            </>
          ) : null}
          {!loading && !isAuthenticated ? (
            <>
              <Link
                href="/login"
                className="btn-tertiary-app hidden px-4 py-2 sm:inline-flex"
              >
                Iniciar sesión
              </Link>
              <Link
                href="/register"
                className="btn-primary-app hidden px-4 py-2 sm:inline-flex"
              >
                Crear cuenta
              </Link>
            </>
          ) : null}
          
          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="rounded-lg p-1.5 transition-all hover:bg-white/70 sm:p-2 lg:hidden"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} />
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="border-t border-border bg-[rgba(255,255,255,0.86)] lg:hidden">
          <nav className="flex flex-col p-4 space-y-2">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                href={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all font-medium text-sm ${
                  isActive(item.path)
                    ? 'btn-primary-app text-[color:var(--color-primary-foreground)] shadow-elevation-md'
                    : 'text-foreground hover:bg-white/70'
                }`}
              >
                <Icon name={item.icon as any} size={20} />
                <span>{item.label}</span>
              </Link>
            ))}
            {!loading && !isAuthenticated ? (
              <>
                <Link
                  href="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="btn-tertiary-app flex justify-center px-3 py-2.5 text-sm"
                >
                  Iniciar sesión
                </Link>
                <Link
                  href="/register"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="btn-primary-app flex justify-center px-3 py-2.5 text-sm"
                >
                  Crear cuenta
                </Link>
              </>
            ) : null}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
